import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import * as XLSX from 'xlsx';
import { toast } from 'react-toastify';
import { FiDownload, FiRefreshCw, FiExternalLink } from 'react-icons/fi';
import { END_POINT, getJwtToken } from '../../Redux/AdminReducer/action';
import { useNavigate } from 'react-router-dom';

const authHeaders = () => ({ headers: { Authorization: getJwtToken() } });

const fmt = (v) => {
  if (!v) return '—';
  const d = new Date(v);
  return isNaN(d.getTime()) ? String(v) : d.toLocaleDateString('en-GB');
};

const patientName = (p) =>
  [p?.firstName, p?.surname].filter(Boolean).join(' ').trim() || '—';

const TABS = [
  { id: 'beforeAfter', label: 'Before / After',  flag: 'BeforeAfterMark' },
  { id: 'caseStudy',   label: 'Case Studies',     flag: 'CaseMark'        },
  { id: 'review',      label: 'Reviews',           flag: 'ReviewMark'      },
  { id: 'courier',     label: 'Courier',           flag: null              },
  { id: 'referral',    label: 'Referral Business', flag: null              },
];

const Badge = ({ children, color = 'slate' }) => {
  const map = {
    green:  'bg-emerald-50 text-emerald-700 border-emerald-200',
    amber:  'bg-amber-50  text-amber-700  border-amber-200',
    rose:   'bg-rose-50   text-rose-700   border-rose-200',
    sky:    'bg-sky-50    text-sky-700    border-sky-200',
    slate:  'bg-slate-50  text-slate-700  border-slate-200',
  };
  return (
    <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs font-semibold ${map[color] || map.slate}`}>
      {children}
    </span>
  );
};

const FLAG_STATUS_OPTIONS = ['Pending', 'In Progress', 'Done', 'Not Required'];
const COURIER_STATUS_OPTIONS = ['Pending', 'Dispatched', 'In Transit', 'Delivered', 'Returned'];

function StatusCell({ value, options, onChange, busy }) {
  return (
    <select
      value={value || ''}
      disabled={busy}
      onChange={(e) => onChange(e.target.value)}
      className="rounded-lg border border-slate-200 bg-white px-2 py-1 text-xs font-medium text-slate-700 outline-none transition hover:border-slate-300 focus:border-sky-500 focus:ring-2 focus:ring-sky-500/20 disabled:opacity-50"
    >
      <option value="">— set status —</option>
      {options.map((o) => (
        <option key={o} value={o}>{o}</option>
      ))}
    </select>
  );
}

function FlagTable({ flag, statusOptions, label }) {
  const navigate = useNavigate();
  const [rows, setRows]       = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch]   = useState('');
  const [busy, setBusy]       = useState(null);
  const debounce              = useRef(null);

  const load = useCallback(async (q = '') => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: 200, page: 1, [flag]: true });
      if (q) params.append('search', q);
      const { data } = await axios.get(
        `${END_POINT}/customer/list/dashboard?${params.toString()}`,
        authHeaders()
      );
      setRows(Array.isArray(data) ? data : data?.data || []);
    } catch {
      toast.error(`Could not load ${label}`);
    } finally {
      setLoading(false);
    }
  }, [flag, label]);

  useEffect(() => {
    clearTimeout(debounce.current);
    debounce.current = setTimeout(() => load(search), 350);
    return () => clearTimeout(debounce.current);
  }, [load, search]);

  const updateStatus = async (patientId, status) => {
    setBusy(patientId);
    try {
      await axios.put(
        `${END_POINT}/customer/updateProfile/${patientId}`,
        { [`${flag}Status`]: status },
        authHeaders()
      );
      setRows((prev) =>
        prev.map((r) => (r._id === patientId ? { ...r, [`${flag}Status`]: status } : r))
      );
      toast.success('Status updated');
    } catch {
      toast.error('Could not update status');
    } finally {
      setBusy(null);
    }
  };

  const downloadXlsx = () => {
    const data = rows.map((r, i) => ({
      '#': i + 1,
      'Patient Name': patientName(r),
      'Patient ID': r.customerId || '—',
      'Mobile': r.mobile || '—',
      'Location': r.location || '—',
      'Status': r[`${flag}Status`] || '—',
    }));
    const ws = XLSX.utils.json_to_sheet(data);
    ws['!cols'] = Object.keys(data[0] || {}).map(() => ({ wch: 22 }));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, label.replace(/\//g, '-'));
    XLSX.writeFile(wb, `${label.replace(/\s+/g, '_')}.xlsx`);
  };

  const filtered = rows.filter((r) => {
    if (!search) return true;
    const s = search.toLowerCase();
    return (
      patientName(r).toLowerCase().includes(s) ||
      String(r.customerId || '').toLowerCase().includes(s) ||
      String(r.mobile || '').includes(s)
    );
  });

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <input
          type="text"
          placeholder="Search by name, patient ID or mobile…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full max-w-xs rounded-xl border border-slate-200 px-3.5 py-2 text-sm outline-none focus:border-sky-500 focus:ring-2 focus:ring-sky-500/20"
        />
        <div className="flex items-center gap-2">
          <button
            onClick={() => load(search)}
            className="inline-flex items-center gap-1.5 rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-medium text-slate-700 transition hover:bg-slate-50"
          >
            <FiRefreshCw className="h-3.5 w-3.5" /> Refresh
          </button>
          <button
            onClick={downloadXlsx}
            disabled={filtered.length === 0}
            className="inline-flex items-center gap-1.5 rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs font-semibold text-emerald-700 transition hover:bg-emerald-100 disabled:opacity-50"
          >
            <FiDownload className="h-3.5 w-3.5" /> Download Excel
          </button>
        </div>
      </div>

      <div className="overflow-x-auto rounded-2xl border border-slate-200 bg-white shadow-sm">
        <table className="w-full min-w-[640px] text-sm">
          <thead className="bg-slate-50 text-xs font-semibold uppercase tracking-wide text-slate-500">
            <tr>
              <th className="px-4 py-3 text-left">#</th>
              <th className="px-4 py-3 text-left">Patient Name</th>
              <th className="px-4 py-3 text-left">Patient ID</th>
              <th className="px-4 py-3 text-left">Mobile</th>
              <th className="px-4 py-3 text-left">Location</th>
              <th className="px-4 py-3 text-left">Status</th>
              <th className="px-4 py-3 text-left">Action</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {loading ? (
              <tr>
                <td colSpan={7} className="px-4 py-10 text-center text-sm text-slate-500">
                  Loading…
                </td>
              </tr>
            ) : filtered.length === 0 ? (
              <tr>
                <td colSpan={7} className="px-4 py-10 text-center text-sm text-slate-400">
                  No {label} patients found.
                </td>
              </tr>
            ) : (
              filtered.map((r, i) => (
                <tr key={r._id} className="transition hover:bg-slate-50/60">
                  <td className="px-4 py-3 text-slate-500">{i + 1}</td>
                  <td className="px-4 py-3 font-medium text-slate-900">{patientName(r)}</td>
                  <td className="px-4 py-3 text-slate-600">{r.customerId || '—'}</td>
                  <td className="px-4 py-3 text-slate-600">{r.mobile || '—'}</td>
                  <td className="px-4 py-3 text-slate-600">{r.location || '—'}</td>
                  <td className="px-4 py-3">
                    {r[`${flag}Status`] ? (
                      <Badge color={
                        r[`${flag}Status`] === 'Done'        ? 'green' :
                        r[`${flag}Status`] === 'In Progress' ? 'amber' :
                        r[`${flag}Status`] === 'Pending'     ? 'sky'   : 'slate'
                      }>
                        {r[`${flag}Status`]}
                      </Badge>
                    ) : (
                      <span className="text-xs text-slate-400">Not set</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <StatusCell
                        value={r[`${flag}Status`]}
                        options={statusOptions}
                        onChange={(v) => updateStatus(r._id, v)}
                        busy={busy === r._id}
                      />
                      <button
                        onClick={() => navigate(`/prescription/add/${r._id}`)}
                        className="rounded-lg border border-slate-200 px-2 py-1 text-xs font-medium text-slate-700 transition hover:bg-slate-50"
                      >
                        Open
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
        {filtered.length > 0 && (
          <div className="border-t border-slate-100 px-4 py-2 text-right text-xs text-slate-400">
            {filtered.length} patient{filtered.length !== 1 ? 's' : ''}
          </div>
        )}
      </div>
    </div>
  );
}

function CourierTable() {
  const navigate  = useNavigate();
  const [rows, setRows]       = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch]   = useState('');
  const [busy, setBusy]       = useState(null);
  const debounce              = useRef(null);

  const load = useCallback(async (q = '') => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: 200, page: 1, hasCourier: true });
      if (q) params.append('search', q);
      const { data } = await axios.get(
        `${END_POINT}/prescription/courier-list?${params.toString()}`,
        authHeaders()
      );
      setRows(Array.isArray(data) ? data : data?.data || []);
    } catch {
      toast.error('Could not load courier prescriptions');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    clearTimeout(debounce.current);
    debounce.current = setTimeout(() => load(search), 350);
    return () => clearTimeout(debounce.current);
  }, [load, search]);

  const updateStatus = async (prescriptionId, status) => {
    setBusy(prescriptionId);
    try {
      await axios.put(
        `${END_POINT}/prescription/courier-status/${prescriptionId}`,
        { courierStatus: status },
        authHeaders()
      );
      setRows((prev) =>
        prev.map((r) => (r._id === prescriptionId ? { ...r, courierStatus: status } : r))
      );
      toast.success('Courier status updated');
    } catch {
      toast.error('Could not update courier status');
    } finally {
      setBusy(null);
    }
  };

  const downloadXlsx = () => {
    const data = rows.map((r, i) => ({
      '#': i + 1,
      'Patient Name': patientName(r.patient || r),
      'Patient ID': r.patient?.customerId || r.customerId || '—',
      'Mobile': r.patient?.mobile || r.mobile || '—',
      'Visit Date': fmt(r.Date),
      'Courier Name': r.CourierDetails?.courierName || '—',
      'Courier Date': r.CourierDetails?.courierDate || '—',
      'Courier Cost': r.CourierDetails?.courierCost || '—',
      'Tracking No.': r.CourierDetails?.trackingId || r.CourierDetails?.trackingNo || '—',
      'Status': r.courierStatus || '—',
    }));
    const ws = XLSX.utils.json_to_sheet(data);
    ws['!cols'] = Object.keys(data[0] || {}).map(() => ({ wch: 22 }));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Courier');
    XLSX.writeFile(wb, 'Courier_Records.xlsx');
  };

  const filtered = rows.filter((r) => {
    if (!search) return true;
    const s = search.toLowerCase();
    const name  = patientName(r.patient || r).toLowerCase();
    const cName = String(r.CourierDetails?.courierName || '').toLowerCase();
    const id    = String(r.patient?.customerId || r.customerId || '').toLowerCase();
    const mob   = String(r.patient?.mobile || r.mobile || '');
    const trk   = String(r.CourierDetails?.trackingId || r.CourierDetails?.trackingNo || '').toLowerCase();
    return name.includes(s) || cName.includes(s) || id.includes(s) || mob.includes(s) || trk.includes(s);
  });

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <input
          type="text"
          placeholder="Search by name, patient ID, courier or mobile…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full max-w-xs rounded-xl border border-slate-200 px-3.5 py-2 text-sm outline-none focus:border-sky-500 focus:ring-2 focus:ring-sky-500/20"
        />
        <div className="flex items-center gap-2">
          <button
            onClick={() => load(search)}
            className="inline-flex items-center gap-1.5 rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-medium text-slate-700 transition hover:bg-slate-50"
          >
            <FiRefreshCw className="h-3.5 w-3.5" /> Refresh
          </button>
          <button
            onClick={downloadXlsx}
            disabled={filtered.length === 0}
            className="inline-flex items-center gap-1.5 rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs font-semibold text-emerald-700 transition hover:bg-emerald-100 disabled:opacity-50"
          >
            <FiDownload className="h-3.5 w-3.5" /> Download Excel
          </button>
        </div>
      </div>

      <div className="overflow-x-auto rounded-2xl border border-slate-200 bg-white shadow-sm">
        <table className="w-full min-w-[900px] text-sm">
          <thead className="bg-slate-50 text-xs font-semibold uppercase tracking-wide text-slate-500">
            <tr>
              <th className="px-4 py-3 text-left">#</th>
              <th className="px-4 py-3 text-left">Patient</th>
              <th className="px-4 py-3 text-left">Patient ID</th>
              <th className="px-4 py-3 text-left">Visit Date</th>
              <th className="px-4 py-3 text-left">Courier Name</th>
              <th className="px-4 py-3 text-left">Courier Date</th>
              <th className="px-4 py-3 text-left">Cost</th>
              <th className="px-4 py-3 text-left">Tracking No.</th>
              <th className="px-4 py-3 text-left">Status</th>
              <th className="px-4 py-3 text-left">Action</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {loading ? (
              <tr>
                <td colSpan={10} className="px-4 py-10 text-center text-sm text-slate-500">
                  Loading…
                </td>
              </tr>
            ) : filtered.length === 0 ? (
              <tr>
                <td colSpan={10} className="px-4 py-10 text-center text-sm text-slate-400">
                  No courier records found. Fill in Courier Details on a prescription to see them here.
                </td>
              </tr>
            ) : (
              filtered.map((r, i) => {
                const cd = r.CourierDetails || {};
                const trackingNum = cd.trackingId || cd.trackingNo;
                const isIndiaPost = /india\s*post/i.test(cd.courierName || '') || /speed\s*post/i.test(cd.courierName || '');
                const trackingUrl = isIndiaPost
                  ? 'https://www.indiapost.gov.in/_layouts/15/dop.portal.tracking/trackconsignment.aspx'
                  : `https://www.dtdc.in/tracking/shipment-tracking.asp?strCnno=${encodeURIComponent(trackingNum || '')}`;

                return (
                  <tr key={r._id} className="transition hover:bg-slate-50/60">
                    <td className="px-4 py-3 text-slate-500">{i + 1}</td>
                    <td className="px-4 py-3 font-medium text-slate-900">{patientName(r.patient || r)}</td>
                    <td className="px-4 py-3 text-slate-600">{r.patient?.customerId || r.customerId || '—'}</td>
                    <td className="px-4 py-3 text-slate-600">{fmt(r.Date)}</td>
                    <td className="px-4 py-3 text-slate-700">
                      <span className="font-medium">{cd.courierName || '—'}</span>
                    </td>
                    <td className="px-4 py-3 text-slate-600">{cd.courierDate || '—'}</td>
                    <td className="px-4 py-3 text-slate-600">{cd.courierCost ? `₹${cd.courierCost}` : '—'}</td>
                    <td className="px-4 py-3 text-slate-600">
                      {trackingNum ? (
                        <a
                          href={trackingUrl}
                          target="_blank"
                          rel="noreferrer"
                          className="inline-flex items-center gap-1 font-semibold text-sky-600 hover:text-sky-800 hover:underline"
                          title="Open official tracking page"
                        >
                          <span>{trackingNum}</span>
                          <FiExternalLink className="h-3.5 w-3.5 text-sky-500" />
                        </a>
                      ) : (
                        '—'
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {r.courierStatus ? (
                        <Badge color={
                          r.courierStatus === 'Delivered'   ? 'green' :
                          r.courierStatus === 'In Transit'  ? 'amber' :
                          r.courierStatus === 'Dispatched'  ? 'sky'   :
                          r.courierStatus === 'Returned'    ? 'rose'  : 'slate'
                        }>
                          {r.courierStatus}
                        </Badge>
                      ) : (
                        <span className="text-xs text-slate-400">Not set</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <StatusCell
                          value={r.courierStatus}
                          options={COURIER_STATUS_OPTIONS}
                          onChange={(v) => updateStatus(r._id, v)}
                          busy={busy === r._id}
                        />
                        <button
                          onClick={() => {
                            const pid = r.patient?._id || r.customerId;
                            if (pid) navigate(`/prescription/add/${pid}`);
                          }}
                          className="rounded-lg border border-slate-200 px-2 py-1 text-xs font-medium text-slate-700 transition hover:bg-slate-50"
                        >
                          Open
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
        {filtered.length > 0 && (
          <div className="border-t border-slate-100 px-4 py-2 text-right text-xs text-slate-400">
            {filtered.length} record{filtered.length !== 1 ? 's' : ''}
          </div>
        )}
      </div>
    </div>
  );
}

const REFERRAL_STATUS_COLORS = {
  uploaded:  'green',
  sent:      'sky',
  pending:   'amber',
  failed:    'rose',
  cancelled: 'slate',
};

function ReferralTable() {
  const navigate = useNavigate();
  const [rows, setRows]       = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch]   = useState('');
  const [busy, setBusy]       = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(
        `${END_POINT}/referral-business/requests?limit=200`,
        authHeaders()
      );
      setRows(Array.isArray(data) ? data : data?.data || []);
    } catch {
      toast.error('Could not load referral requests');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const act = async (id, verb) => {
    setBusy(id);
    try {
      const { data } = await axios.post(
        `${END_POINT}/referral-business/requests/${id}/${verb}`,
        {},
        authHeaders()
      );
      const updated = data?.data;
      setRows((prev) => prev.map((r) => (r._id === id ? { ...r, ...(updated || {}) } : r)));
      toast.success(verb === 'resend' ? 'Invite re-sent' : 'Request cancelled');
      if (!updated) load();
    } catch {
      toast.error(verb === 'resend' ? 'Could not re-send invite' : 'Could not cancel request');
    } finally {
      setBusy(null);
    }
  };

  const downloadXlsx = () => {
    const data = rows.map((r, i) => ({
      '#': i + 1,
      'Patient Name': r.patientName || '—',
      'Referral Business': r.businessName || '—',
      'Business Email': r.businessEmail || '—',
      'Tests': (r.tests || []).join(', ') || '—',
      'Status': r.status || '—',
      'Sent On': fmt(r.linkSentAt),
      'Reports': (r.documents || []).length,
      'Requested On': fmt(r.createdAt),
    }));
    const ws = XLSX.utils.json_to_sheet(data);
    ws['!cols'] = Object.keys(data[0] || {}).map(() => ({ wch: 22 }));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Referral Business');
    XLSX.writeFile(wb, 'Referral_Business_Records.xlsx');
  };

  const filtered = rows.filter((r) => {
    if (!search) return true;
    const s = search.toLowerCase();
    return (
      String(r.patientName || '').toLowerCase().includes(s) ||
      String(r.businessName || '').toLowerCase().includes(s) ||
      String(r.businessEmail || '').toLowerCase().includes(s) ||
      String(r.status || '').toLowerCase().includes(s) ||
      (r.tests || []).some((t) => String(t).toLowerCase().includes(s))
    );
  });

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <input
          type="text"
          placeholder="Search by patient, business, test or status…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full max-w-xs rounded-xl border border-slate-200 px-3.5 py-2 text-sm outline-none focus:border-sky-500 focus:ring-2 focus:ring-sky-500/20"
        />
        <div className="flex items-center gap-2">
          <button
            onClick={() => load()}
            className="inline-flex items-center gap-1.5 rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-medium text-slate-700 transition hover:bg-slate-50"
          >
            <FiRefreshCw className="h-3.5 w-3.5" /> Refresh
          </button>
          <button
            onClick={downloadXlsx}
            disabled={filtered.length === 0}
            className="inline-flex items-center gap-1.5 rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs font-semibold text-emerald-700 transition hover:bg-emerald-100 disabled:opacity-50"
          >
            <FiDownload className="h-3.5 w-3.5" /> Download Excel
          </button>
        </div>
      </div>

      <div className="overflow-x-auto rounded-2xl border border-slate-200 bg-white shadow-sm">
        <table className="w-full min-w-[900px] text-sm">
          <thead className="bg-slate-50 text-xs font-semibold uppercase tracking-wide text-slate-500">
            <tr>
              <th className="px-4 py-3 text-left">#</th>
              <th className="px-4 py-3 text-left">Patient</th>
              <th className="px-4 py-3 text-left">Referral Business</th>
              <th className="px-4 py-3 text-left">Tests Advised</th>
              <th className="px-4 py-3 text-left">Requested On</th>
              <th className="px-4 py-3 text-left">Sent On</th>
              <th className="px-4 py-3 text-left">Reports</th>
              <th className="px-4 py-3 text-left">Status</th>
              <th className="px-4 py-3 text-left">Action</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {loading ? (
              <tr>
                <td colSpan={9} className="px-4 py-10 text-center text-sm text-slate-500">
                  Loading…
                </td>
              </tr>
            ) : filtered.length === 0 ? (
              <tr>
                <td colSpan={9} className="px-4 py-10 text-center text-sm text-slate-400">
                  No referral requests found. Advise a pathological test against a referral centre on a prescription to see it here.
                </td>
              </tr>
            ) : (
              filtered.map((r, i) => (
                <tr key={r._id} className="transition hover:bg-slate-50/60">
                  <td className="px-4 py-3 text-slate-500">{i + 1}</td>
                  <td className="px-4 py-3 font-medium text-slate-900">{r.patientName || '—'}</td>
                  <td className="px-4 py-3 text-slate-700">
                    <span className="font-medium">{r.businessName || '—'}</span>
                    {r.businessEmail ? (
                      <div className="text-xs text-slate-400">{r.businessEmail}</div>
                    ) : null}
                  </td>
                  <td className="px-4 py-3 text-slate-600">{(r.tests || []).join(', ') || '—'}</td>
                  <td className="px-4 py-3 text-slate-600">{fmt(r.createdAt)}</td>
                  <td className="px-4 py-3 text-slate-600">{fmt(r.linkSentAt)}</td>
                  <td className="px-4 py-3 text-slate-600">{(r.documents || []).length || '—'}</td>
                  <td className="px-4 py-3">
                    {r.status ? (
                      <Badge color={REFERRAL_STATUS_COLORS[r.status] || 'slate'}>{r.status}</Badge>
                    ) : (
                      <span className="text-xs text-slate-400">Not set</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => act(r._id, 'resend')}
                        disabled={busy === r._id || r.status === 'cancelled'}
                        className="rounded-lg border border-sky-200 bg-sky-50 px-2 py-1 text-xs font-semibold text-sky-700 transition hover:bg-sky-100 disabled:opacity-50"
                      >
                        Resend
                      </button>
                      <button
                        onClick={() => act(r._id, 'cancel')}
                        disabled={busy === r._id || r.status === 'cancelled'}
                        className="rounded-lg border border-slate-200 px-2 py-1 text-xs font-medium text-slate-700 transition hover:bg-slate-50 disabled:opacity-50"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={() => {
                          if (r.patientId) navigate(`/prescription/add/${r.patientId}`);
                        }}
                        className="rounded-lg border border-slate-200 px-2 py-1 text-xs font-medium text-slate-700 transition hover:bg-slate-50"
                      >
                        Open
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
        {filtered.length > 0 && (
          <div className="border-t border-slate-100 px-4 py-2 text-right text-xs text-slate-400">
            {filtered.length} record{filtered.length !== 1 ? 's' : ''}
          </div>
        )}
      </div>
    </div>
  );
}

export default function PrescriptionFlagsTable() {
  const [activeTab, setActiveTab] = useState('beforeAfter');

  const tabConfig = {
    beforeAfter: { flag: 'BeforeAfterMark', label: 'Before / After', statusOptions: FLAG_STATUS_OPTIONS },
    caseStudy:   { flag: 'CaseMark',        label: 'Case Studies',   statusOptions: FLAG_STATUS_OPTIONS },
    review:      { flag: 'ReviewMark',       label: 'Reviews',        statusOptions: FLAG_STATUS_OPTIONS },
  };

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap gap-1 rounded-2xl border border-slate-200 bg-slate-50 p-1">
        {TABS.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex-1 min-w-[120px] rounded-xl px-4 py-2 text-sm font-semibold transition ${
              activeTab === tab.id
                ? 'bg-white text-sky-700 shadow-sm border border-slate-200'
                : 'text-slate-500 hover:text-slate-700'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {activeTab === 'courier' ? (
        <CourierTable />
      ) : activeTab === 'referral' ? (
        <ReferralTable />
      ) : (
        <FlagTable
          key={activeTab}
          flag={tabConfig[activeTab].flag}
          statusOptions={tabConfig[activeTab].statusOptions}
          label={tabConfig[activeTab].label}
        />
      )}
    </div>
  );
}
